import React, { useState, useEffect, useMemo } from 'react';
import { getAllMunicipalities, Municipality, FEE_CATEGORIES, DEFAULT_ENABLED_CATEGORIES } from '@/lib/data';
import { getYearsAsNumbers, getLatestYear } from '@/config/years';
import { WidgetEmbed, WidgetConfig, WidgetType } from './WidgetEmbed';
import { Button } from '@/components/ui/button';
import { Check, Code, Search, Share2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

const WIDGET_LABELS: Record<WidgetType, string> = {
  stats: 'Nøkkeltall',
  kommuner: 'Kommunesammenligning',
  fylker: 'Fylkesoversikt',
  topp: 'Toppliste',
  badge: 'Enkeltbadge',
  compact: 'Kompakt (én linje)',
  breakdown: 'Avgiftsfordeling',
  mini: 'Mini-stolper',
};

const WIDGET_DESCRIPTIONS: Record<WidgetType, string> = {
  stats: 'Høyeste, laveste og gjennomsnitt',
  kommuner: 'Sammenlign kommuner med stolpediagram',
  fylker: 'Gjennomsnitt per fylke',
  topp: 'Topp N kommuner',
  badge: 'Én kommune, ett stort tall',
  compact: '4 kommuner på én linje',
  breakdown: 'Fordeling av avgifter for én kommune',
  mini: 'Tynde stolper for rask sammenligning',
};

export const WidgetConfigurator: React.FC = () => {
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [type, setType] = useState<WidgetType>('stats');
  const [year, setYear] = useState(getLatestYear());
  const [show, setShow] = useState<('highest' | 'lowest' | 'average' | 'increase')[]>(['highest', 'lowest', 'average', 'increase']);
  const [categories, setCategories] = useState<string[]>(DEFAULT_ENABLED_CATEGORIES);
  const [selectedKommuner, setSelectedKommuner] = useState<string[]>([]);
  const [selectedFylker, setSelectedFylker] = useState<string[]>([]);
  const [limit, setLimit] = useState(5);
  const [sort, setSort] = useState<'asc' | 'desc'>('desc');
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [bgColor, setBgColor] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [kommuneSearch, setKommuneSearch] = useState('');
  const [kommuneInputFocused, setKommuneInputFocused] = useState(false);

  useEffect(() => {
    getAllMunicipalities().then(setMunicipalities);
  }, []);

  const counties = [...new Set(municipalities.map(m => m.county))].filter(Boolean).sort();

  const singleKommuneTypes: WidgetType[] = ['badge', 'breakdown'];
  const needsSingleKommune = singleKommuneTypes.includes(type);

  const config: WidgetConfig = {
    type,
    year,
    show,
    kommuner:
      needsSingleKommune && selectedKommuner.length > 0
        ? [selectedKommuner[0]]
        : selectedKommuner.length > 0
        ? selectedKommuner
        : undefined,
    fylker: selectedFylker.length > 0 ? selectedFylker : undefined,
    limit,
    sort,
    theme,
    bgColor: bgColor.trim() || undefined,
    categories,
  };

  const buildUrl = () => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const params = new URLSearchParams();
    params.set('embed', '1');
    params.set('type', type);
    params.set('ar', String(year));
    params.set('theme', theme);
    if (bgColor.trim()) params.set('bg', bgColor.trim().replace(/^#/, ''));
    if (show.length > 0) params.set('show', show.join(','));
    if (selectedKommuner.length > 0) {
      params.set('kommuner', (needsSingleKommune ? [selectedKommuner[0]] : selectedKommuner).join(','));
    }
    if (selectedFylker.length > 0) params.set('fylker', selectedFylker.join(','));
    if (categories.length > 0) params.set('kategorier', categories.join(','));
    if (type === 'topp' || type === 'mini') {
      params.set('limit', String(limit));
      if (type === 'topp') params.set('sort', sort);
    }
    return `${origin}/del?${params.toString()}`;
  };

  const embedCode = `<iframe src="${buildUrl()}" width="100%" height="460" frameborder="0" style="border:none;border-radius:12px;" title="Kommunale avgifter"></iframe>`;

  const handleCopyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('textarea');
      input.value = embedCode;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const toggleShow = (s: 'highest' | 'lowest' | 'average' | 'increase') => {
    setShow(prev => (prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]));
  };

  const toggleCategory = (id: string) => {
    setCategories(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const toggleKommune = (id: string) => {
    if (needsSingleKommune) {
      setSelectedKommuner(prev => (prev.includes(id) ? [] : [id]));
    } else {
      setSelectedKommuner(prev =>
        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id].slice(0, 10)
      );
    }
  };

  const addKommune = (id: string) => {
    if (needsSingleKommune) {
      setSelectedKommuner([id]);
    } else if (!selectedKommuner.includes(id) && selectedKommuner.length < 10) {
      setSelectedKommuner([...selectedKommuner, id]);
    }
    setKommuneSearch('');
  };

  const filteredKommuner = useMemo(() => {
    let list = municipalities.filter((m) => !selectedKommuner.includes(m.id));
    if (selectedFylker.length > 0) {
      list = list.filter((m) => selectedFylker.includes(m.county));
    }
    if (!kommuneSearch.trim()) return list.slice(0, 15);
    const q = kommuneSearch.toLowerCase().trim();
    return list
      .filter((m) => {
        const name = m.name.toLowerCase();
        const county = m.county.toLowerCase();
        if (name.includes(q) || county.includes(q)) return true;
        if (`${name} kommune`.includes(q) || name.replace(' kommune', '').includes(q)) return true;
        const words = q.split(/\s+/);
        if (words.length > 1) {
          return words.every((w) => name.includes(w) || county.includes(w) || w.length < 3);
        }
        return false;
      })
      .slice(0, 12);
  }, [municipalities, selectedKommuner, selectedFylker, kommuneSearch]);

  const canAddMore = needsSingleKommune
    ? selectedKommuner.length < 1
    : selectedKommuner.length < 10;

  const toggleFylke = (county: string) => {
    setSelectedFylker(prev =>
      prev.includes(county) ? prev.filter(x => x !== county) : [...prev, county]
    );
  };

  const years = getYearsAsNumbers().sort((a, b) => b - a);

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-100 space-y-6">
          <div>
            <Label className="mb-1 block">Widget-type</Label>
            <Select value={type} onValueChange={(v) => setType(v as WidgetType)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(WIDGET_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v} — {WIDGET_DESCRIPTIONS[k as WidgetType]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="mb-1 block">År</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block">Utseende</Label>
              <Select value={theme} onValueChange={(v) => setTheme(v as 'light' | 'dark')}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">Lys</SelectItem>
                  <SelectItem value="dark">Mørk</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="mb-1 block">Bakgrunnsfarge på siden</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={bgColor || '#ffffff'}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="h-9 w-12 cursor-pointer rounded border border-gray-200 bg-white p-0.5"
                  title="Velg farge"
                />
                <Input
                  type="text"
                  placeholder="#ffffff eller tom for standard"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="h-9 w-36 font-mono text-sm"
                />
                {bgColor && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setBgColor('')} className="h-9">
                    Tøm
                  </Button>
                )}
              </div>
              <p className="text-xs text-warmGray mt-1">Fargen rundt widgeten når den vises i iframe</p>
            </div>
          </div>

          {type === 'stats' && (
            <div className="space-y-2">
              <Label className="mb-1 block">Vis tall</Label>
              <div className="flex flex-wrap gap-4 mt-1">
                {(['highest', 'lowest', 'average', 'increase'] as const).map((s) => (
                  <label key={s} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={show.includes(s)} onCheckedChange={() => toggleShow(s)} />
                    <span className="text-sm">
                      {s === 'highest' ? 'Høyeste' : s === 'lowest' ? 'Laveste' : s === 'average' ? 'Gj.snitt' : 'Økning'}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {(type === 'topp' || type === 'mini') && (
            <div>
              <Label className="mb-1 block">Antall (1–20)</Label>
              <Input
                type="number"
                min={1}
                max={20}
                value={limit}
                onChange={(e) =>
                  setLimit(Math.min(20, Math.max(1, parseInt(e.target.value) || 5)))
                }
                className="mt-1 w-24"
              />
            </div>
          )}

          {type === 'topp' && (
            <div>
              <Label className="mb-1 block">Sortering</Label>
              <Select value={sort} onValueChange={(v) => setSort(v as 'asc' | 'desc')}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">Dyreste først</SelectItem>
                  <SelectItem value="asc">Billigste først</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="mb-1 block">Avgifter som inkluderes</Label>
            <p className="text-xs text-warmGray mb-2">Velg hvilke avgiftstyper widgeten skal summere og vise</p>
            <div className="flex flex-wrap gap-2">
              {FEE_CATEGORIES.map((category) => (
                <label
                  key={category.id}
                  className={`flex items-center gap-1.5 cursor-pointer px-2 py-1 rounded-md text-sm ${
                    categories.includes(category.id)
                      ? 'bg-nordicBlue/10 text-nordicBlue'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <Checkbox
                    checked={categories.includes(category.id)}
                    onCheckedChange={() => toggleCategory(category.id)}
                  />
                  <span>{category.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="mb-1 block">Fylker (valgfritt)</Label>
            <p className="text-xs text-warmGray mb-2">Tom = hele Norge</p>
            <div className="flex flex-wrap gap-2">
              {counties.map((c) => (
                <label
                  key={c}
                  className={`flex items-center gap-1.5 cursor-pointer px-2 py-1 rounded-md text-sm ${
                    selectedFylker.includes(c)
                      ? 'bg-nordicBlue/10 text-nordicBlue'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <Checkbox
                    checked={selectedFylker.includes(c)}
                    onCheckedChange={() => toggleFylke(c)}
                  />
                  <span>{c}</span>
                </label>
              ))}
            </div>
          </div>

          {(type === 'kommuner' || type === 'topp' || type === 'badge' || type === 'breakdown' || type === 'mini') && (
            <div>
              <Label className="mb-1 block">
                Kommuner
                {needsSingleKommune ? ' (velg 1)' : ' (valgfritt, max 10)'}
              </Label>
              <p className="text-xs text-warmGray mb-2">
                {needsSingleKommune
                  ? 'Badge og fordeling krever én kommune.'
                  : 'Tom = automatisk utvalg'}
              </p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-warmGray pointer-events-none" />
                <Input
                  type="text"
                  placeholder="Søk og velg kommune..."
                  className="pl-9"
                  value={kommuneSearch}
                  onChange={(e) => setKommuneSearch(e.target.value)}
                  onFocus={() => setKommuneInputFocused(true)}
                  onBlur={() => setTimeout(() => setKommuneInputFocused(false), 150)}
                  disabled={!canAddMore}
                />
                {canAddMore && (kommuneSearch.length > 0 || kommuneInputFocused) && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                    {filteredKommuner.length > 0 ? (
                      <div className="max-h-52 overflow-y-auto">
                        {filteredKommuner.map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            className="w-full text-left px-3 py-2.5 hover:bg-nordicBlue/5 flex justify-between items-center text-sm"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              addKommune(m.id);
                            }}
                          >
                            <span className="font-medium text-deepCharcoal">{m.name}</span>
                            <span className="text-xs text-warmGray">{m.county}</span>
                          </button>
                        ))}
                      </div>
                    ) : kommuneSearch.length > 0 ? (
                      <div className="px-3 py-4 text-sm text-warmGray">
                        Ingen treff. Prøv et annet søkeord.
                      </div>
                    ) : (
                      <div className="px-3 py-3 text-xs text-warmGray">
                        Skriv for å søke blant kommuner
                        {selectedFylker.length > 0 && ' (filtrert etter valgte fylker)'}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {selectedKommuner.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {selectedKommuner.map((id) => {
                    const m = municipalities.find((x) => x.id === id);
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-nordicBlue/10 text-nordicBlue text-sm"
                      >
                        {m?.name || id}
                        <button
                          type="button"
                          onClick={() => toggleKommune(id)}
                          className="hover:text-deepCharcoal rounded p-0.5"
                          aria-label={`Fjern ${m?.name}`}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-100">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <Share2 className="h-5 w-5 mr-2 text-nordicBlue" />
              Forhåndsvisning
            </h2>
            <div
              className={`rounded-lg p-4 min-h-[300px] ${!bgColor.trim() ? 'bg-slate-50' : ''}`}
              style={
                bgColor.trim()
                  ? { backgroundColor: bgColor.trim().startsWith('#') ? bgColor.trim() : '#' + bgColor.trim() }
                  : undefined
              }
            >
              <WidgetEmbed config={config} baseUrl="/" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-100">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <Code className="h-5 w-5 mr-2 text-nordicBlue" />
              Iframe-kode
            </h2>
            <pre className="text-xs bg-gray-50 p-3 rounded-lg overflow-x-auto max-h-28 overflow-y-auto border border-gray-100 font-mono">
              {embedCode}
            </pre>
            <Button
              variant={copied ? 'default' : 'outline'}
              size="sm"
              className="mt-2 gap-2"
              onClick={handleCopyEmbed}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4" />
                  Kopiert!
                </>
              ) : (
                <>
                  <Code className="h-4 w-4" />
                  Kopier kode
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
